// RITA-1520 (Nutzungserfassung Phase 3b, Epic RITA-1496) — Wutklick-Automat.
//
// Definition (Ticket RITA-1500, Entwurf v2 nach Prüfer-Panel): drei und mehr Klicks auf DASSELBE
// Ziel binnen einer Sekunde. „Ziel“ ist der Klick-Schlüssel, den auch das click-Ereignis trägt
// (clickTarget.ts) bzw. der Totklick-Schlüssel (deadClick.ts) — nie ein DOM-Knoten, nie Text.
//
// Der Automat ist rein: er bekommt Schlüssel, Art, Zeitpunkt und Ort, hält je Ziel die
// Zeitstempel des laufenden Fensters und meldet je Ausbruch GENAU EINMAL (beim Erreichen der
// Schwelle). Weitere Klicks verlängern den Ausbruch stumm; erst ein leeres Fenster oder ein
// Zielwechsel beendet ihn.
//
// Drei Regeln gegen Rauschen (Panel-Befunde, Linse Frontend/Fachnutzen):
//  1. Gleitendes Fenster über die Zeitstempel: nur Klicks der letzten windowMs zählen. Wer nach zwei
//     zögerlichen Klicks noch dreimal schnell klickt, ist wütend — ein Fenster ab dem ersten Klick
//     würde das verpassen; ein reiner Abstand zum vorigen Klick ließe eine 900-ms-Kette endlos laufen.
//  2. Ort: der meldende Klick muss nahe (maxDistancePx) beim ersten Klick des Fensters liegen —
//     schnelles Durchklicken verschiedener Tabellenzellen desselben Schlüssels ist kein Wutklick.
//  3. Dämpfung: höchstens EIN Ausbruch je Ziel je cooldownMs. Wutklicks sind bündelig; ohne
//     Dämpfung stellte eine einzelne genervte Person über 50 % der Zeilenmenge, die Dominanzregel
//     der Lese-API ließe die Zeile fallen und übrig bliebe eine bezeichnerlose Sammelzeile — eine
//     Zahl ohne Ort. Die Dämpfung entschärft das an der Quelle und ist fachlich ehrlicher:
//     ein genervter Mensch = ein Vorfall.
// Ob ein Klick überhaupt zählt (echter Zeigerklick: isTrusted, detail > 0) und ob beim dritten Klick
// gerade Text markiert wird, prüft die Laufzeit VOR bzw. NACH dem Aufruf — der Automat kennt kein DOM.
export const RAGE_CLICK_THRESHOLD = 3;
export const RAGE_CLICK_WINDOW_MS = 1000;
export const RAGE_CLICK_MAX_DISTANCE_PX = 40;
export const RAGE_CLICK_COOLDOWN_MS = 60000;

/** Höchstens so viele Ziele merkt sich die Dämpfung (älteste fallen weg). */
const MAX_COOLDOWN_KEYS = 50;

export interface RageClickOptions {
  threshold?: number;
  windowMs?: number;
  maxDistancePx?: number;
  cooldownMs?: number;
}

export interface ClickSample {
  at: number;
  x: number;
  y: number;
}

export interface RageBurst {
  key: string;
  kind: string;
  /** Klicks im Fenster zum Zeitpunkt der Meldung (mindestens die Schwelle). */
  clicks: number;
}

export interface RageClickDetector {
  /** Einen Klick vermerken; liefert den Ausbruch genau beim Erreichen der Schwelle, sonst null. */
  note(key: string, kind: string, sample: ClickSample): RageBurst | null;
  reset(): void;
}

const distance = (a: ClickSample, b: ClickSample): number => Math.hypot(a.x - b.x, a.y - b.y);

export const createRageClickDetector = ({
  threshold = RAGE_CLICK_THRESHOLD,
  windowMs = RAGE_CLICK_WINDOW_MS,
  maxDistancePx = RAGE_CLICK_MAX_DISTANCE_PX,
  cooldownMs = RAGE_CLICK_COOLDOWN_MS,
}: RageClickOptions = {}): RageClickDetector => {
  let key: string | null = null;
  let kind = '';
  let samples: ClickSample[] = [];
  let reported = false;
  const lastReportAt = new Map<string, number>();

  const rememberReport = (reportedKey: string, at: number): void => {
    lastReportAt.delete(reportedKey);
    lastReportAt.set(reportedKey, at);
    if (lastReportAt.size > MAX_COOLDOWN_KEYS) {
      const oldest = lastReportAt.keys().next().value;
      if (oldest !== undefined) lastReportAt.delete(oldest);
    }
  };

  const startBurst = (nextKey: string, nextKind: string): void => {
    key = nextKey;
    kind = nextKind;
    samples = [];
    reported = false;
  };

  return {
    note(nextKey: string, nextKind: string, sample: ClickSample): RageBurst | null {
      const last = samples[samples.length - 1];
      // Zielwechsel oder Pause länger als das Fenster: neuer Ausbruch.
      if (nextKey !== key || (last !== undefined && sample.at - last.at > windowMs)) {
        startBurst(nextKey, nextKind);
      }
      samples.push(sample);
      // Nur Klicks im Fenster zählen; ältere fallen weg, ohne den Ausbruch zu beenden.
      samples = samples.filter((s) => sample.at - s.at <= windowMs);
      if (reported || samples.length < threshold) return null;
      // Der meldende Klick muss nahe beim ersten Klick des Fensters liegen.
      if (distance(samples[0], sample) > maxDistancePx) return null;
      // Dämpfung je Ziel.
      const lastReport = lastReportAt.get(nextKey);
      if (lastReport !== undefined && sample.at - lastReport < cooldownMs) {
        reported = true;
        return null;
      }
      reported = true;
      rememberReport(nextKey, sample.at);
      return { key: nextKey, kind, clicks: samples.length };
    },
    reset(): void {
      startBurst('', '');
      key = null;
      lastReportAt.clear();
    },
  };
};
